import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UtensilsCrossed, Mail, Lock, Eye, EyeOff, Shield, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const { login } = useAuth();
  const { setCurrentScreen } = useApp();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Clear any customer session before admin login
      localStorage.removeItem('bookfood_customer_session');
      
      const success = await login(email, password);
      if (success) {
        setCurrentScreen('admin-dashboard');
      } else {
        setError('Invalid email or password');
      }
      setIsLoading(false);
    } catch (err) {
      setError('Login failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0A0B] via-[#1C1C1E] to-[#0A0A0B] flex flex-col relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-96 h-96 rounded-full mix-blend-screen filter blur-3xl opacity-20"
          style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-96 h-96 rounded-full mix-blend-screen filter blur-3xl opacity-15"
          style={{ background: 'radial-gradient(circle, #CD7F32 0%, transparent 70%)' }}
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Header */}
      <div className="pt-16 pb-12 px-6 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div 
            className="w-28 h-28 mx-auto mb-6 rounded-full flex items-center justify-center shadow-2xl relative border-2"
            style={{ 
              background: 'linear-gradient(135deg, #D4AF37 0%, #CD7F32 100%)',
              borderColor: 'rgba(212, 175, 55, 0.5)',
              boxShadow: '0 0 40px rgba(212, 175, 55, 0.3), 0 8px 32px rgba(0, 0, 0, 0.5)'
            }}
            whileHover={{ scale: 1.05, rotate: 5 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Shield size={48} className="text-[#0A0A0B]" strokeWidth={2.5} />
            <motion.div
              className="absolute -top-2 -right-2"
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles size={24} className="text-[#D4AF37]" fill="#D4AF37" />
            </motion.div>
          </motion.div>
          <motion.h1 
            className="text-5xl font-bold mb-3"
            style={{
              fontFamily: "'Playfair Display', serif",
              background: 'linear-gradient(135deg, #D4AF37 0%, #F4E4C1 50%, #D4AF37 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '2px',
              textShadow: '0 0 30px rgba(212, 175, 55, 0.3)'
            }}
            animate={{ backgroundPosition: ['0%', '100%', '0%'] }}
            transition={{ duration: 5, repeat: Infinity }}
          >
            Admin Portal
          </motion.h1>
          <motion.p
            className="text-[#D4AF37] text-sm tracking-[4px] uppercase mb-2"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Management Access
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-[#F5F5DC] text-lg"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Secure Staff Login
          </motion.p>
        </motion.div>
      </div>

      {/* Login Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="flex-1 px-6 max-w-md mx-auto w-full relative z-10"
      >
        <motion.form
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onSubmit={handleLogin}
          className="space-y-6 backdrop-blur-xl p-8 rounded-2xl shadow-2xl"
          style={{
            background: 'rgba(44, 44, 46, 0.7)',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(212, 175, 55, 0.2)'
          }}
        >
          <div>
            <label className="block text-base font-semibold mb-3" style={{ color: '#F5F5DC' }}>
              Admin Email
            </label>
            <motion.div
              className="relative"
              animate={{
                scale: emailFocused ? 1.02 : 1,
              }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Mail 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200"
                style={{ color: emailFocused ? '#D4AF37' : '#AEAEB2' }}
                size={22} 
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                className="w-full pl-12 pr-4 py-4 rounded-xl text-white text-base transition-all duration-200"
                style={{
                  background: 'rgba(28, 28, 30, 0.6)',
                  border: `2px solid ${emailFocused ? '#D4AF37' : 'rgba(212, 175, 55, 0.3)'}`,
                  outline: 'none'
                }}
                placeholder="admin@hotelhub.com"
                required
              />
            </motion.div>
          </div>

          <div>
            <label className="block text-base font-semibold mb-3" style={{ color: '#F5F5DC' }}>
              Password
            </label>
            <motion.div
              className="relative"
              animate={{
                scale: passwordFocused ? 1.02 : 1,
              }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Lock 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200"
                style={{ color: passwordFocused ? '#D4AF37' : '#AEAEB2' }}
                size={22} 
              />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                className="w-full pl-12 pr-12 py-4 rounded-xl text-white text-base transition-all duration-200"
                style={{
                  background: 'rgba(28, 28, 30, 0.6)',
                  border: `2px solid ${passwordFocused ? '#D4AF37' : 'rgba(212, 175, 55, 0.3)'}`,
                  outline: 'none'
                }}
                placeholder="Enter admin password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200"
                style={{ color: '#AEAEB2' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#D4AF37'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#AEAEB2'}
              >
                {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
              </button>
            </motion.div>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-red-400 text-sm text-center bg-red-900 bg-opacity-30 p-4 rounded-xl border border-red-500 border-opacity-50 backdrop-blur-sm"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            type="submit"
            disabled={isLoading}
            whileHover={{ scale: 1.02, boxShadow: "0 0 30px rgba(212, 175, 55, 0.5)" }}
            whileTap={{ scale: 0.98 }}
            className="w-full btn-primary py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group"
          >
            <span className="relative z-10">
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="inline-block w-5 h-5 border-2 border-t-transparent rounded-full"
                    style={{ borderColor: '#0A0A0B', borderTopColor: 'transparent' }}
                  />
                  Authenticating...
                </span>
              ) : (
                'Access Admin Panel'
              )}
            </span>
          </motion.button>
        </motion.form>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {/* Demo Credentials */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8 p-6 backdrop-blur-xl rounded-2xl shadow-xl"
            style={{
              background: 'rgba(44, 44, 46, 0.5)',
              border: '1px solid rgba(212, 175, 55, 0.2)'
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Shield size={18} style={{ color: '#D4AF37' }} />
              <p className="text-sm font-semibold" style={{ color: '#F5F5DC' }}>Demo Admin Access</p>
            </div>
            <div className="space-y-2">
              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-center gap-2 text-sm p-3 rounded-lg"
                style={{ background: 'rgba(28, 28, 30, 0.4)' }}
              >
                <span className="font-semibold min-w-[80px]" style={{ color: '#D4AF37' }}>Manager:</span>
                <span style={{ color: '#AEAEB2' }}>akxay@gmail.com / 123123123</span>
              </motion.div>
              
            </div>
          </motion.div>

          {/* Customer Login Link */}
          <div className="mt-6 text-center">
            <p className="text-base mb-2" style={{ color: '#AEAEB2' }}>
              Are you a customer?
            </p>
            <motion.button 
              onClick={() => {
                // Clear admin session when switching to customer login
                localStorage.removeItem('bookfood_user');
                setCurrentScreen('customer-login');
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="font-semibold transition-colors duration-200 px-6 py-2 rounded-lg"
              style={{ 
                color: '#D4AF37',
                background: 'rgba(212, 175, 55, 0.1)',
                border: '1px solid rgba(212, 175, 55, 0.3)'
              }}
            >
              ← Customer Login
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;